import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { HpRespCollectionsModuleItemOld } from './HpRespCollectionsModuleItemOld';
import { HpRespModuleContainerOld } from '../HpRespModuleContainerOld/HpRespModuleContainerOld';
import { RenderIn, device } from '../../utils/RenderIn';

import styles from './HpRespCollectionsModuleOld.scss';
import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import {
    trackCollectionsImpression,
    trackCollectionsItemClick,
    trackArrowClick,
} from './helpers/CollectionsTrackingOld';
import { slugify } from '../../utils/slugify';

function HpRespCollectionsModuleOldComponent({ homepage }) {
    if (!homepage.collectionsModule) {
        return null;
    }

    const { title, items } = homepage.collectionsModule;
    const dataTn = `${slugify(title)}-container`;

    return (
        <HpRespModuleContainerOld dataTn={dataTn}>
            <HpRespModuleContainerOld.SubHeader>{title}</HpRespModuleContainerOld.SubHeader>
            <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                {() => {
                    const itemsPerPage = 5;

                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackCollectionsImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handleIndexChange }) => (
                                <Carousel
                                    slideCarouselItems
                                    classNames={{ wrapper: styles.wrapper }}
                                    totalItems={items.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({ index, isVisible }) => (
                                        <HpRespCollectionsModuleItemOld
                                            item={items[index]}
                                            isVisible={isVisible}
                                            onClick={() =>
                                                trackCollectionsItemClick({
                                                    item: items[index],
                                                    index,
                                                })
                                            }
                                        />
                                    )}
                                    onIndexChange={handleIndexChange}
                                    onLeftArrowClick={() => trackArrowClick({ type: 'previous' })}
                                    onRightArrowClick={() => trackArrowClick({ type: 'next' })}
                                />
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
            <RenderIn deviceTypes={[device.MOBILE]}>
                {() => {
                    const itemsPerPage = 3.5;
                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackCollectionsImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handlePageChange }) => (
                                <Swiper
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={(currentPage, previousPage) => {
                                        trackArrowClick({
                                            type: previousPage > currentPage ? 'previous' : 'next',
                                        });
                                        handlePageChange(currentPage);
                                    }}
                                >
                                    {items.map((item, index) => (
                                        <HpRespCollectionsModuleItemOld
                                            key={item.cmsId}
                                            item={item}
                                            onClick={() =>
                                                trackCollectionsItemClick({ item, index })
                                            }
                                        />
                                    ))}
                                </Swiper>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
        </HpRespModuleContainerOld>
    );
}

HpRespCollectionsModuleOldComponent.propTypes = {
    homepage: PropTypes.object.isRequired,
};

export const HpRespCollectionsModuleOld = createFragmentContainer(
    HpRespCollectionsModuleOldComponent,
    {
        homepage: graphql`
            fragment HpRespCollectionsModuleOld_homepage on Homepage {
                collectionsModule: module(type: "collections") {
                    ... on CollectionsModule {
                        title
                        items {
                            cmsId
                            title
                            link
                            imageUrl
                            isLinkable
                        }
                    }
                }
            }
        `,
    }
);
